import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Mail, Phone, MessageSquare, Send,
  Activity, Layers, Plus, CreditCard, Tag, ClipboardList, User as UserIcon,
} from "lucide-react";
import { formatApiError } from "./AuthContext";
import { ClientStatusPill } from "./ClientsList";
import TasksSection from "./TasksSection";
import SmsSection from "./SmsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_STATUSES = ["Active", "Paused", "Completed", "Cancelled"];
const AU_STATES = ["", "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-1";

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingMsg, setBillingMsg] = useState("");
  const [assign, setAssign] = useState({ serviceId: "", startDate: "", monthlyValue: "" });
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/clients/${id}`),
        axios.get(`${API}/admin/services`),
      ]);
      setClient(c.data);
      setCatalog((s.data.services || []).filter((x) => x.active));
      setBilling({
        businessName: c.data.businessName || "",
        contactFirstName: c.data.contactFirstName || "",
        contactLastName: c.data.contactLastName || "",
        email: c.data.email || "",
        phone: c.data.phone || "",
        abn: c.data.abn || "",
        billingEmail: c.data.billingEmail || "",
        street: c.data.billingAddress?.street || "",
        suburb: c.data.billingAddress?.suburb || "",
        state: c.data.billingAddress?.state || "",
        postcode: c.data.billingAddress?.postcode || "",
        paymentTerms: c.data.paymentTerms || "",
        status: c.data.status || "Active",
      });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveBilling = async (e) => {
    e.preventDefault();
    setBillingSaving(true);
    setBillingMsg("");
    try {
      await axios.patch(`${API}/admin/clients/${id}`, {
        businessName: billing.businessName,
        contactFirstName: billing.contactFirstName,
        contactLastName: billing.contactLastName,
        email: billing.email || undefined,
        phone: billing.phone,
        abn: billing.abn,
        billingEmail: billing.billingEmail,
        billingAddress: {
          street: billing.street, suburb: billing.suburb,
          state: billing.state, postcode: billing.postcode,
        },
        paymentTerms: billing.paymentTerms,
        status: billing.status,
      });
      setBillingMsg("Saved.");
      await load();
    } catch (err) {
      setBillingMsg(formatApiError(err));
    } finally {
      setBillingSaving(false);
    }
  };

  const assignService = async (e) => {
    e.preventDefault();
    if (!assign.serviceId) return;
    setAssigning(true);
    setAssignError("");
    try {
      await axios.post(`${API}/admin/clients/${id}/services`, {
        serviceId: assign.serviceId,
        startDate: assign.startDate,
        monthlyValue: assign.monthlyValue === "" ? null : Number(assign.monthlyValue),
      });
      setAssign({ serviceId: "", startDate: "", monthlyValue: "" });
      await load();
    } catch (err) {
      setAssignError(formatApiError(err));
    } finally {
      setAssigning(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/admin/clients/${id}/notes`, { body: note.trim() });
      setNote("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-mid-neutral">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading client…
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
        <div className="mt-2">
          <Link to="/admin/clients" className="text-forest font-semibold underline">Back to clients</Link>
        </div>
      </div>
    );
  }

  if (!client) return null;

  const setB = (k) => (e) => setBilling({ ...billing, [k]: e.target.value });

  return (
    <div data-testid="admin-client-detail" className="space-y-6">
      <Link
        to="/admin/clients"
        data-testid="client-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-forest"
      >
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </Link>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <header className="rounded-2xl bg-white border border-border-neutral p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-mint">Client</p>
                <h1 className="mt-1 text-2xl md:text-3xl font-extrabold text-forest">
                  {client.businessName || `${client.contactFirstName} ${client.contactLastName}`}
                </h1>
                {(client.contactFirstName || client.contactLastName) && (
                  <p className="mt-1 text-mid-neutral flex items-center gap-1.5">
                    <UserIcon className="h-3.5 w-3.5" /> {client.contactFirstName} {client.contactLastName}
                  </p>
                )}
              </div>
              <ClientStatusPill status={client.status} />
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
              <ContactRow Icon={Mail} value={client.email} link={`mailto:${client.email}`} />
              <ContactRow Icon={Phone} value={client.phone} link={`tel:${client.phone}`} />
            </div>

            {client.leadId && (
              <Link
                to={`/admin/leads/${client.leadId}`}
                data-testid="client-source-lead"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-forest hover:underline"
              >
                <ClipboardList className="h-4 w-4 text-mint" /> View source lead
              </Link>
            )}
          </header>

          {/* Services */}
          <section className="rounded-2xl bg-white border border-border-neutral p-6">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
              <Layers className="h-4 w-4 text-mint" /> Services
            </h2>

            <div className="mt-4 space-y-3">
              {(client.services || []).length === 0 && (
                <p className="text-sm text-mid-neutral">No services assigned yet.</p>
              )}
              {(client.services || []).map((a) => (
                <AssignmentRow key={a.id} a={a} onSaved={load} onError={setAssignError} />
              ))}
            </div>

            {assignError && (
              <p data-testid="assign-error" className="mt-3 text-sm text-red-700">{assignError}</p>
            )}

            <form onSubmit={assignService} className="mt-5 rounded-xl border border-dashed border-border-neutral p-4">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral">Assign a service</p>
              <div className="mt-3 grid sm:grid-cols-3 gap-3">
                <select
                  value={assign.serviceId}
                  onChange={(e) => setAssign({ ...assign, serviceId: e.target.value })}
                  data-testid="assign-service-select"
                  className={inputCls + " bg-white"}
                >
                  <option value="">Select service…</option>
                  {catalog.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={assign.startDate}
                  onChange={(e) => setAssign({ ...assign, startDate: e.target.value })}
                  data-testid="assign-start-date"
                  className={inputCls + " bg-white"}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Monthly value (AUD)"
                  value={assign.monthlyValue}
                  onChange={(e) => setAssign({ ...assign, monthlyValue: e.target.value })}
                  data-testid="assign-monthly-value"
                  className={inputCls + " bg-white"}
                />
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="submit"
                  disabled={!assign.serviceId || assigning}
                  data-testid="assign-service-submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                  {assigning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Assign service
                </button>
              </div>
            </form>
          </section>

          {/* Activity timeline */}
          <section className="rounded-2xl bg-white border border-border-neutral p-6">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
              <Activity className="h-4 w-4 text-mint" /> Activity
            </h2>

            <form onSubmit={addNote} className="mt-4">
              <textarea
                data-testid="client-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Add a note or call log…"
                className="w-full p-3 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={!note.trim() || posting}
                  data-testid="client-note-submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                  {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Add note
                </button>
              </div>
            </form>

            <div className="mt-5 space-y-4">
              {(client.timeline || []).length === 0 && (
                <p className="text-sm text-mid-neutral text-center py-4">No activity yet.</p>
              )}
              {(client.timeline || []).map((t) => (
                <div key={t.id} className="flex gap-3">
                  <div className="mt-1">
                    <div className={
                      "h-8 w-8 rounded-full inline-flex items-center justify-center " +
                      (t.type === "note" ? "bg-forest text-lime" : "bg-lime text-forest")
                    }>
                      {t.type === "note" ? <MessageSquare className="h-3.5 w-3.5" /> : <Tag className="h-3.5 w-3.5" />}
                    </div>
                  </div>
                  <div className="flex-1 rounded-xl bg-soft/40 p-3">
                    <p className="text-xs text-mid-neutral">
                      <strong className="text-forest">{t.authorName || t.author}</strong> · {formatTs(t.createdAt)}
                    </p>
                    <p className="mt-1 text-sm text-forest whitespace-pre-wrap">{t.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <TasksSection relatedType="client" relatedId={client.id} />

          <SmsSection relatedType="client" relatedId={client.id} phone={client.phone} />
        </div>

        {/* Sidebar — billing & details */}
        <aside className="space-y-5">
          <div className="rounded-2xl bg-white border border-border-neutral p-5">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">
              <CreditCard className="h-3.5 w-3.5" /> Billing & details
            </p>
            {billing && (
              <form onSubmit={saveBilling} className="mt-4 space-y-3">
                <div>
                  <label className={labelCls}>Business name</label>
                  <input value={billing.businessName} onChange={setB("businessName")} data-testid="billing-business-name" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>First name</label>
                    <input value={billing.contactFirstName} onChange={setB("contactFirstName")} data-testid="billing-first-name" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Last name</label>
                    <input value={billing.contactLastName} onChange={setB("contactLastName")} data-testid="billing-last-name" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={billing.email} onChange={setB("email")} data-testid="billing-email" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input value={billing.phone} onChange={setB("phone")} data-testid="billing-phone" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>ABN</label>
                  <input value={billing.abn} onChange={setB("abn")} placeholder="16 338 218 680" data-testid="billing-abn" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Billing email</label>
                  <input value={billing.billingEmail} onChange={setB("billingEmail")} placeholder="accounts@…" data-testid="billing-billing-email" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Street</label>
                  <input value={billing.street} onChange={setB("street")} data-testid="billing-street" className={inputCls} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className={labelCls}>Suburb</label>
                    <input value={billing.suburb} onChange={setB("suburb")} data-testid="billing-suburb" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>State</label>
                    <select value={billing.state} onChange={setB("state")} data-testid="billing-state" className={inputCls}>
                      {AU_STATES.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Postcode</label>
                    <input value={billing.postcode} onChange={setB("postcode")} data-testid="billing-postcode" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Payment terms</label>
                  <input value={billing.paymentTerms} onChange={setB("paymentTerms")} placeholder="e.g. 14 days" data-testid="billing-payment-terms" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select value={billing.status} onChange={setB("status")} data-testid="billing-status" className={inputCls}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                {billingMsg && (
                  <p data-testid="billing-msg" className="text-xs font-semibold text-forest">{billingMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={billingSaving}
                  data-testid="billing-save"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold disabled:opacity-50"
                >
                  {billingSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Save details
                </button>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function AssignmentRow({ a, onSaved, onError }) {
  const [draft, setDraft] = useState({
    status: a.status,
    startDate: a.startDate || "",
    monthlyValue: a.monthlyValue ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/client-services/${a.id}`, {
        status: draft.status,
        startDate: draft.startDate,
        ...(draft.monthlyValue !== "" ? { monthlyValue: Number(draft.monthlyValue) } : {}),
      });
      onSaved();
    } catch (err) {
      onError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`assignment-${a.serviceKey}`} className="rounded-xl bg-soft/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="font-bold text-forest">{a.serviceName}</p>
        <select
          value={draft.status}
          onChange={(e) => setDraft({ ...draft, status: e.target.value })}
          data-testid={`assignment-status-${a.serviceKey}`}
          className="p-2 rounded-lg bg-white border border-border-neutral text-xs font-semibold text-forest"
        >
          {SERVICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Start date</label>
          <input
            type="date"
            value={draft.startDate}
            onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
            data-testid={`assignment-start-${a.serviceKey}`}
            className={inputCls + " bg-white"}
          />
        </div>
        <div>
          <label className={labelCls}>Monthly value (AUD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.monthlyValue}
            onChange={(e) => setDraft({ ...draft, monthlyValue: e.target.value })}
            data-testid={`assignment-value-${a.serviceKey}`}
            className={inputCls + " bg-white"}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`assignment-save-${a.serviceKey}`}
          className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-3 py-1.5 text-xs font-bold disabled:opacity-50"
        >
          {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
}

function ContactRow({ Icon, value, link }) {
  if (!value) return null;
  const content = (
    <span className="inline-flex items-center gap-2 text-forest">
      <Icon className="h-3.5 w-3.5 text-mint shrink-0" />
      <span className="truncate">{value}</span>
    </span>
  );
  return link ? <a href={link} className="hover:underline">{content}</a> : content;
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
